import { epochSecondsToDate, z } from '@customerio/hydra-validation';
import type { Audience as ApiAudience } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Audience entity — an ad-network audience synced from a segment (workspace-scoped).
 *
 * `id` and `segment_id` are coerced to `string` (layer-wide convention; the wire
 * ships numbers), and the epoch timestamps become `Date`. `meta` is left as the
 * raw backend map (`Record<string, unknown>`) — its internal keys aren't in the
 * spec, so consumers read them explicitly.
 */
export type Audience = Omit<ApiAudience, 'id' | 'segment_id' | 'created_at' | 'updated_at'> & {
  id: string;
  segment_id: string;
  created_at: Date;
  updated_at: Date;
};

const AudienceFromApi = readSchema<Audience>(
  z.looseObject({
    id: z.number().transform(String),
    segment_id: z.number().transform(String),
    created_at: epochSecondsToDate,
    updated_at: epochSecondsToDate,
  }),
);

/** @public */
export const audience = defineEntity<Audience>({
  name: 'audience',
  scope: 'workspace',
  pk: (record) => record.id,
  schema: { read: AudienceFromApi },
});

export const audienceKeys = audience.keys;

export function parseAudience(raw: ApiAudience): Audience {
  return audience.parse(raw);
}
